/**
 * TimeSeparators component renders vertical lines at time boundaries
 * (hours, days, weeks, months, years) based on the visible time range.
 */

import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { isTimestampInGap } from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";
import { type TimeSeparator, type TimeSeparatorType } from "~types/types";

/**
 * TimeSeparators renders vertical guide lines at time boundaries.
 * Subscribes to 'scales' and 'viewport' events for position updates.
 */
const TimeSeparatorsInner: React.FC = () => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["scales", "viewport"]);

  const separatorLines = useMemo(() => {
    const state = storeRef.current.getState();

    // Only render if showTimeSeparators is enabled
    if (state.dataConfig?.showTimeSeparators === false) {
      return [];
    }

    const separators = storeRef.current.getTimeSeparators();
    const scales = storeRef.current.getScales();
    const layout = storeRef.current.getLayout();

    // Get all gaps to filter out separators inside gaps (for single mode)
    const allGaps = Array.from(state.dataGaps.values()).flat();
    const largeGaps = allGaps.filter((g) => g.isLarge);

    return separators
      .filter((sep: TimeSeparator) => {
        // Skip separators that fall inside large gaps in single mode
        if (state.mode === "single" && largeGaps.length > 0) {
          return !isTimestampInGap(sep.timestamp, largeGaps);
        }

        return true;
      })
      .map((sep: TimeSeparator) => {
        const x = scales.x.scale(sep.timestamp);

        // Only include if within visible area
        if (
          x < layout.priceArea.x ||
          x > layout.priceArea.x + layout.priceArea.width
        ) {
          return null;
        }

        return {
          timestamp: sep.timestamp,
          x,
          y1: layout.priceArea.y,
          y2: layout.priceArea.y + layout.priceArea.height,
          label: sep.label,
          type: sep.type,
        };
      })
      .filter(
        (
          line
        ): line is {
          timestamp: number;
          x: number;
          y1: number;
          y2: number;
          label: string;
          type: TimeSeparatorType;
        } => line !== null
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  if (separatorLines.length === 0) {
    return null;
  }

  return (
    <g className={styles.timeSeparators}>
      {separatorLines.map((line) => (
        <line
          key={line.timestamp}
          className={styles.timeSeparatorLine}
          x1={line.x}
          y1={line.y1}
          x2={line.x}
          y2={line.y2}
        />
      ))}
    </g>
  );
};

export const TimeSeparators = React.memo(TimeSeparatorsInner);
TimeSeparators.displayName = "TimeSeparators";
